<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>HaioFlash Worker Nodes</h2>
        <vs-button color="primary" @click="showDialog = true">
          <feather-icon icon="PlusIcon" class="mr-2"></feather-icon>
          Add Node
        </vs-button>
      </div>
      
      <!-- Filters -->
      <vs-row class="mb-4">
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.name"
            class="w-full"
            label-placeholder="Node name"
            @keyup.enter="loadNodes()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.region_id"
            class="w-full"
            placeholder="Region"
            @change="loadNodes()"
          >
            <vs-select-item value="" text="All Regions"></vs-select-item>
            <vs-select-item
              v-for="region in availableRegions"
              :key="region.id"
              :value="region.id"
              :text="region.name"
            ></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.is_active"
            class="w-full"
            placeholder="Status"
            @change="loadNodes()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item :value="1" text="Active"></vs-select-item>
            <vs-select-item :value="0" text="Inactive"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="12" vs-xs="12">
          <vs-button color="primary" class="w-full" @click="loadNodes()">
            <feather-icon icon="SearchIcon"></feather-icon>
          </vs-button>
        </vs-col>
      </vs-row>
      
      <!-- Nodes Table -->
      <vs-table v-if="nodes" :data="nodes">
        <template slot="thead">
          <vs-th>ID</vs-th>
          <vs-th>Name</vs-th>
          <vs-th>IP Address</vs-th>
          <vs-th>Region</vs-th>
          <vs-th>VMs</vs-th>
          <vs-th>CPU Usage</vs-th>
          <vs-th>RAM Usage</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Health</vs-th>
          <vs-th>Actions</vs-th>
        </template>
        
        <template slot-scope="{ data }">
          <vs-tr :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.id }}</vs-td>
            <vs-td>{{ tr.name }}</vs-td>
            <vs-td>{{ tr.ip_address }}</vs-td>
            <vs-td>{{ tr.region_name }}</vs-td>
            <vs-td>{{ tr.current_vms || 0 }} / {{ tr.max_vms || 0 }} ({{ tr.running_vms || 0 }} running)</vs-td>
            <vs-td>
              <small class="text-grey">N/A</small>
            </vs-td>
            <vs-td>
              <small class="text-grey">N/A</small>
            </vs-td>
            <vs-td>
              <vs-chip :color="tr.is_available ? 'success' : 'danger'">
                {{ tr.status }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <vs-chip :color="getHealthColor(tr.status)">
                {{ tr.status || 'Unknown' }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <vs-button
                size="small"
                color="success"
                type="border"
                icon="check_circle"
                @click="checkNodeHealth(tr.id)"
                title="Check Health"
              ></vs-button>
              <vs-button
                size="small"
                color="primary"
                type="border"
                icon="edit"
                @click="editNode(tr)"
              ></vs-button>
              <vs-button
                size="small"
                :color="tr.is_active ? 'warning' : 'success'"
                type="border"
                icon="power_settings_new"
                @click="toggleNodeStatus(tr.id)"
              ></vs-button>
              <vs-button
                size="small"
                color="danger"
                type="border"
                icon="delete"
                @click="deleteNode(tr.id)"
              ></vs-button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
    </vx-card>
    
    <!-- Create/Edit Dialog -->
    <vs-popup
      title="Node Details"
      :active.sync="showDialog"
    >
      <div class="p-4">
        <vs-row>
          <vs-col vs-w="12">
            <vs-input
              v-model="formData.name"
              class="w-full mb-3"
              label="Node Name"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model="formData.ip_address"
              class="w-full mb-3"
              label="IP Address"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.port"
              class="w-full mb-3"
              label="Port"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="12">
            <vs-select
              v-model="formData.region_id"
              class="w-full mb-3"
              label="Region"
            >
              <vs-select-item
                v-for="region in availableRegions"
                :key="region.id"
                :value="region.id"
                :text="region.name"
              ></vs-select-item>
            </vs-select>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.max_vms"
              class="w-full mb-3"
              label="Max VMs"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.total_cpu_cores"
              class="w-full mb-3"
              label="Total CPU Cores"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.total_ram_mb"
              class="w-full mb-3"
              label="Total RAM (MB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="6" vs-sm="12">
            <vs-input
              v-model.number="formData.total_storage_gb"
              class="w-full mb-3"
              label="Total Storage (GB)"
              type="number"
            ></vs-input>
          </vs-col>
          
          <vs-col vs-w="12">
            <vs-switch v-model="formData.is_active" class="mb-3">
              <span slot="on">Active</span>
              <span slot="off">Inactive</span>
            </vs-switch>
          </vs-col>
          
          <vs-col vs-w="12" class="flex justify-end">
            <vs-button
              color="danger"
              type="border"
              @click="showDialog = false"
              class="mr-2"
            >
              Cancel
            </vs-button>
            <vs-button
              color="primary"
              @click="saveNode()"
            >
              {{ formData.id ? 'Update' : 'Create' }}
            </vs-button>
          </vs-col>
        </vs-row>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'NodesList',
  data() {
    return {
      nodes: [],
      availableRegions: [],
      showDialog: false,
      filters: {
        name: '',
        region_id: '',
        is_active: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1
      },
      formData: {
        id: null,
        name: '',
        ip_address: '',
        port: 8080,
        region_id: null,
        max_vms: 100,
        total_cpu_cores: 32,
        total_ram_mb: 65536,
        total_storage_gb: 1000,
        is_active: true
      }
    }
  },
  mounted() {
    this.loadNodes()
    this.loadRegions()
  },
  methods: {
    async loadNodes() {
      try {
        const params = {
          ...this.filters
        }
        console.log('Loading nodes with params:', params)
        const response = await this.$http.get('/admin/haioflash/nodes', { params })
        console.log('Nodes response:', response.data)
        console.log('Nodes data:', response.data.data)
        console.log('Is array:', Array.isArray(response.data.data))
        if (response && response.data && response.data.success) {
          this.nodes = Array.isArray(response.data.data) ? response.data.data : []
          console.log('Nodes loaded:', this.nodes)
        } else {
          this.nodes = []
        }
      } catch (error) {
        console.error('Error loading nodes:', error)
        console.error('Error response:', error.response)
        this.nodes = []
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load nodes',
          color: 'danger'
        })
        console.error(error)
      }
    },
    
    async loadRegions() {
      try {
        const { data } = await this.$http.get('/admin/haioflash/regions')
        this.availableRegions = data.data || []
      } catch (error) {
        console.error('Failed to load regions', error)
      }
    },
    
    editNode(node) {
      this.formData = { ...node }
      this.showDialog = true
    },
    
    async saveNode() {
      try {
        if (this.formData.id) {
          await this.$http.put(`/admin/haioflash/nodes/${this.formData.id}`, this.formData)
          this.$vs.notify({
            title: 'Success',
            text: 'Node updated successfully',
            color: 'success'
          })
        } else {
          await this.$http.post('/admin/haioflash/nodes', this.formData)
          this.$vs.notify({
            title: 'Success',
            text: 'Node created successfully',
            color: 'success'
          })
        }
        this.showDialog = false
        this.resetForm()
        this.loadNodes()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to save node',
          color: 'danger'
        })
        console.error(error)
      }
    },
    
    async checkNodeHealth(id) {
      try {
        this.$vs.loading()
        const { data } = await this.$http.post(`/admin/haioflash/nodes/${id}/health-check`)
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Health Check',
          text: `Node health: ${data.status}`,
          color: data.status === 'healthy' ? 'success' : 'warning'
        })
        this.loadNodes()
      } catch (error) {
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to check node health',
          color: 'danger'
        })
      }
    },
    
    async toggleNodeStatus(id) {
      try {
        await this.$http.post(`/admin/haioflash/nodes/${id}/toggle-active`)
        this.$vs.notify({
          title: 'Success',
          text: 'Node status updated',
          color: 'success'
        })
        this.loadNodes()
      } catch (error) {
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to update node status',
          color: 'danger'
        })
      }
    },
    
    async deleteNode(id) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this node?',
        accept: async () => {
          try {
            await this.$http.delete(`/admin/haioflash/nodes/${id}`)
            this.$vs.notify({
              title: 'Success',
              text: 'Node deleted successfully',
              color: 'success'
            })
            this.loadNodes()
          } catch (error) {
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete node',
              color: 'danger'
            })
          }
        }
      })
    },
    
    getUsageColor(usage) {
      if (usage < 60) return 'success'
      if (usage < 80) return 'warning'
      return 'danger'
    },
    
    getHealthColor(status) {
      const colors = {
        'healthy': 'success',
        'degraded': 'warning',
        'unhealthy': 'danger'
      }
      return colors[status] || 'dark'
    },
    
    resetForm() {
      this.formData = {
        id: null,
        name: '',
        ip_address: '',
        port: 8080,
        region_id: null,
        max_vms: 100,
        total_cpu_cores: 32,
        total_ram_mb: 65536,
        total_storage_gb: 1000,
        is_active: true
      }
    }
  }
}
</script>
